'use client';

/** @jsxImportSource @emotion/react */
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

import {
  Chip,
  Container,
  FlexItem,
  FlexLayout,
  Icon,
  IconContent,
  Image,
  Steps,
  Typography,
} from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useTranslations } from 'next-intl';
import { GoArrowRight } from 'react-icons/go';

import { useThemeMode } from '@/context';

import { useStyles } from './WhosItFor.styles';

const STEP_CONFIG = [
  {
    id: 'step-1',
    step: 1,
    labelKey: 'steps.step1.label',
    descriptionKey: 'steps.step1.description',
    painTodayKey: 'steps.step1.painToday',
    whyWciFitsKey: 'steps.step1.whyWciFits',
  },
  {
    id: 'step-2',
    step: 2,
    labelKey: 'steps.step2.label',
    descriptionKey: 'steps.step2.description',
    painTodayKey: 'steps.step2.painToday',
    whyWciFitsKey: 'steps.step2.whyWciFits',
  },
  {
    id: 'step-3',
    step: 3,
    labelKey: 'steps.step3.label',
    descriptionKey: 'steps.step3.description',
    painTodayKey: 'steps.step3.painToday',
    whyWciFitsKey: 'steps.step3.whyWciFits',
  },
  {
    id: 'step-4',
    step: 4,
    labelKey: 'steps.step4.label',
    descriptionKey: 'steps.step4.description',
    painTodayKey: 'steps.step4.painToday',
    whyWciFitsKey: 'steps.step4.whyWciFits',
  },
  {
    id: 'step-5',
    step: 5,
    labelKey: 'steps.step5.label',
    descriptionKey: 'steps.step5.description',
    painTodayKey: 'steps.step5.painToday',
    whyWciFitsKey: 'steps.step5.whyWciFits',
  },
] as const;

const WhosItFor = () => {
  const t = useTranslations('WhosItFor');
  const { isDark } = useThemeMode();
  const styles = useStyles(isDark);

  const measureRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [desktopContentHeight, setDesktopContentHeight] = useState(0);

  const BulletItem = ({ children }: { children: ReactNode }) => (
    <FlexLayout css={styles.bulletRow} padding={{ default: 0 }}>
      <FlexItem>
        <IconContent
          icon={<Icon name={GoArrowRight} color="#FF3B30" size={{ default: 16, lg: 20 }} />}
          iconPlacement="left"
          withWrap
          iconVerticalAlignment="start"
          iconOffset={{ md: 1, lg: 4 }}
        >
          <Typography variant="p" css={styles.bulletText}>
            {children}
          </Typography>
        </IconContent>
      </FlexItem>
    </FlexLayout>
  );

  const MobileBulletItem = ({ children }: { children: ReactNode }) => (
    <FlexLayout
      padding={{ default: 0 }}
      alignItems="flex-start"
      gap={{ default: 8 }}
      direction="row"
    >
      <FlexItem>
        <IconContent
          icon={<Icon name={GoArrowRight} color="#FF3B30" size={{ default: 16 }} />}
          iconPlacement="left"
          withWrap
          iconVerticalAlignment="start"
          iconOffset={{ default: 2 }}
        >
          <Typography variant="p" css={styles.bulletText}>
            {children}
          </Typography>
        </IconContent>
      </FlexItem>
    </FlexLayout>
  );

  const getSources = (step: number) => [
    {
      media: '(min-width: 1024px)',
      srcSet: `/images/whosItForStep${step}Desktop.png`,
    },
    {
      media: '(min-width: 768px)',
      srcSet: `/images/whosItForStep${step}Tablet.png`,
    },
    {
      media: '(min-width: 320px)',
      srcSet: `/images/whosItForStep${step}Mobile.png`,
    },
  ];

  const WhosItForImage = ({ step }: { step: number }) => (
    <div css={styles.stableImageFrame}>
      <Image
        isImgDecorative={false}
        src={`/images/whosItForStep${step}Mobile.svg`}
        sources={getSources(step)}
        width="100%"
        height="100%"
        altText={`Whos it for step ${step}`}
        objectFit="cover"
        borderRadius="20px"
        loading="lazy"
      />
    </div>
  );

  const DesktopDetails = ({
    step,
    painTodayKey,
    whyWciFitsKey,
  }: {
    step: number;
    painTodayKey: (typeof STEP_CONFIG)[number]['painTodayKey'];
    whyWciFitsKey: (typeof STEP_CONFIG)[number]['whyWciFitsKey'];
  }) => (
    <FlexLayout
      direction={{ default: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems="flex-start"
      gap={16}
      css={styles.twoColumnLayout}
      padding={{ default: 0 }}
    >
      <FlexItem
        css={[
          styles.leftColumn,
          step === 5 &&
            css`
              min-width: 0;
              flex: 0.65;
            `,
        ]}
      >
        <Typography variant="h3" css={styles.sectionTitle}>
          {t('painTodayHeadingText')}
        </Typography>

        {t.raw(painTodayKey).map((item: string) => (
          <BulletItem key={item}>{item}</BulletItem>
        ))}
      </FlexItem>

      <FlexItem css={styles.rightColumn}>
        <Typography variant="h3" css={styles.sectionTitle}>
          {t('whyWciFitsHeadingText')}
        </Typography>

        <Typography variant="p" css={styles.sectionDescription}>
          {t(whyWciFitsKey)}
        </Typography>
      </FlexItem>
    </FlexLayout>
  );

  useLayoutEffect(() => {
    let frameId = 0;

    const calculateHeight = () => {
      cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const heights = measureRefs.current
          .filter((element): element is HTMLDivElement => element !== null)
          .map((element) => element.scrollHeight);

        if (heights.length === 0) {
          return;
        }

        const tallestHeight = Math.ceil(Math.max(...heights)) + 10;

        setDesktopContentHeight((currentHeight) =>
          currentHeight === tallestHeight ? currentHeight : tallestHeight,
        );
      });
    };

    calculateHeight();

    const resizeObserver = new ResizeObserver(calculateHeight);

    measureRefs.current.forEach((element) => {
      if (element) {
        resizeObserver.observe(element);
      }
    });

    window.addEventListener('resize', calculateHeight);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  const DesktopContent = ({
    step,
    painTodayKey,
    whyWciFitsKey,
  }: {
    step: number;
    painTodayKey: (typeof STEP_CONFIG)[number]['painTodayKey'];
    whyWciFitsKey: (typeof STEP_CONFIG)[number]['whyWciFitsKey'];
  }) => (
    <FlexLayout
      direction="column"
      alignItems="center"
      justifyContent="center"
      padding={{ md: 0, lg: 0 }}
      gap={{ md: 16, lg: 24 }}
    >
      <FlexItem css={styles.imageWrapper}>
        <WhosItForImage step={step} />
      </FlexItem>

      <FlexItem>
        <div
          css={styles.desktopContentWrapper}
          {...(desktopContentHeight > 0 && {
            style: {
              minHeight: `${desktopContentHeight}px`,
            },
          })}
        >
          <DesktopDetails step={step} painTodayKey={painTodayKey} whyWciFitsKey={whyWciFitsKey} />
        </div>
      </FlexItem>
    </FlexLayout>
  );

  const MobileContent = ({
    step,
    labelKey,
    descriptionKey,
    painTodayKey,
    whyWciFitsKey,
  }: {
    step: number;
    labelKey: (typeof STEP_CONFIG)[number]['labelKey'];
    descriptionKey: (typeof STEP_CONFIG)[number]['descriptionKey'];
    painTodayKey: (typeof STEP_CONFIG)[number]['painTodayKey'];
    whyWciFitsKey: (typeof STEP_CONFIG)[number]['whyWciFitsKey'];
  }) => (
    <FlexLayout
      direction="column"
      alignItems="center"
      justifyContent="center"
      {...(step !== 1 && { gap: 19 })}
      padding={{ default: 0 }}
    >
      <FlexItem css={styles.imageWrapper}>
        <WhosItForImage step={step} />
      </FlexItem>

      <FlexLayout direction="column" gap={4} padding={{ default: 0 }}>
        <FlexItem>
          <Typography variant="h3" customStyles={styles.mobileLabel}>
            {t(labelKey)}
          </Typography>
        </FlexItem>

        <FlexItem>
          <Typography variant="p" customStyles={styles.mobileCommonText}>
            {t(descriptionKey)}
          </Typography>
        </FlexItem>

        <FlexItem>
          <Typography variant="h3" customStyles={styles.mobilePainToday}>
            {t('painTodayHeadingText')}
          </Typography>

          <FlexLayout gap={12} direction="column" padding={{ default: 0 }}>
            {t.raw(painTodayKey).map((item: string) => (
              <MobileBulletItem key={item}>{item}</MobileBulletItem>
            ))}
          </FlexLayout>
        </FlexItem>

        <FlexItem>
          <Typography variant="h3" customStyles={styles.mobileWhyWciFitsHeader}>
            {t('whyWciFitsHeadingText')}
          </Typography>

          <Typography customStyles={styles.mobileWhyWciFitsDescription}>
            {t(whyWciFitsKey)}
          </Typography>
        </FlexItem>
      </FlexLayout>
    </FlexLayout>
  );

  const stepsData = STEP_CONFIG.map((step) => ({
    id: step.id,
    label: t(step.labelKey),
    description: t(step.descriptionKey),
    content: (
      <DesktopContent
        step={step.step}
        painTodayKey={step.painTodayKey}
        whyWciFitsKey={step.whyWciFitsKey}
      />
    ),
    mobileContent: (
      <MobileContent
        step={step.step}
        labelKey={step.labelKey}
        descriptionKey={step.descriptionKey}
        painTodayKey={step.painTodayKey}
        whyWciFitsKey={step.whyWciFitsKey}
      />
    ),
  }));

  return (
    <section css={styles.wrapper} id="who-its-for">
      <Container css={styles.container}>
        <FlexLayout
          direction="column"
          alignItems="center"
          justifyContent="center"
          gap={{ default: 0 }}
          padding={{ default: 0 }}
        >
          <FlexItem>
            <Chip
              label={t('chip')}
              enableHoverEffect={false}
              customStyles={{
                chipContainer: styles.titleChipContainer,
                label: styles.titleChipLabel,
              }}
            />
          </FlexItem>

          <FlexItem css={styles.headerSection}>
            <FlexLayout direction="column" alignItems="center" gap={16} padding={{ default: 0 }}>
              <Typography variant="h3" css={styles.title}>
                {t('title')}
              </Typography>

              <Typography variant="p" css={styles.description}>
                {t('description')}
              </Typography>
            </FlexLayout>
          </FlexItem>

          <FlexLayout
            alignItems="center"
            justifyContent="center"
            css={styles.stepsContainer}
            padding={{ default: 0 }}
          >
            <FlexItem css={styles.stepsWrapper}>
              <Steps
                steps={stepsData}
                mediaSlotRatio={50}
                variant="linear-gradient"
                customStyles={{
                  wrapper: styles.stepsWrapper,
                  mediaSlot: styles.mediaSlot,
                  label: styles.label,
                  description: styles.sectionDescription,
                  indicator: styles.hoverIndicator,
                  mobileStackItem: styles.mobileStackItem,
                }}
              />
            </FlexItem>
          </FlexLayout>
        </FlexLayout>

        <div css={styles.measurementContainer} aria-hidden="true">
          {STEP_CONFIG.map((step, index) => (
            <div
              key={step.id}
              ref={(element) => {
                measureRefs.current[index] = element;
              }}
              css={styles.measurementItem}
            >
              <DesktopDetails
                step={step.step}
                painTodayKey={step.painTodayKey}
                whyWciFitsKey={step.whyWciFitsKey}
              />
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default WhosItFor;
